/**
 * Extrae la primera URL de imagen útil de los datos de una página de guía
 * para usarla como cover/thumbnail (ej. en tarjetas de "Mis guías").
 *
 * Busca en: canvasImages, images, coverImage, dias/days, backgroundImage,
 * stickers, y como último recurso hace una búsqueda recursiva por URLs de imagen.
 *
 * @param {object} pageData - page.data del backend (una página de guía)
 * @param {string} apiOrigin - Origen del API para URLs relativas (ej. http://localhost:3001)
 * @returns {string|null} URL de imagen o null
 */
export function extractFirstPageCoverImage(pageData, apiOrigin = "") {
	if (!pageData || typeof pageData !== "object") return null;

	const toAbsolute = (url) => {
		if (!url || typeof url !== "string") return null;
		url = url.trim();
		if (!url) return null;
		// Descartar data:image/svg mínimos o placeholders
		if (url.startsWith("data:image/svg")) return null;
		if (url.startsWith("data:")) return url;
		if (url.startsWith("http://") || url.startsWith("https://")) return url;
		if (apiOrigin && url.startsWith("/"))
			return `${apiOrigin.replace(/\/$/, "")}${url}`;
		return url;
	};

	// 1) images[] — los templates guardan { id, src } o { id, url } o { id, imageUrl }
	const images = pageData.images;
	if (Array.isArray(images) && images.length > 0) {
		for (const img of images) {
			const u =
				typeof img === "string"
					? img
					: img?.src ?? img?.url ?? img?.imageUrl;
			if (u) {
				const abs = toAbsolute(u);
				if (abs) return abs;
			}
		}
	}

	// 2) canvasImages[] — { id, imageUrl } o { id, url }
	const canvasImages = pageData.canvasImages;
	if (Array.isArray(canvasImages) && canvasImages.length > 0) {
		for (const img of canvasImages) {
			const u = img?.imageUrl ?? img?.url ?? img?.src;
			if (u) {
				const abs = toAbsolute(u);
				if (abs) return abs;
			}
		}
	}

	// 3) coverImage / image / backgroundImage a nivel raíz
	for (const key of ["coverImage", "image", "backgroundImage", "bgImage", "portada"]) {
		const val = pageData[key];
		if (val && typeof val === "string") {
			const abs = toAbsolute(val);
			if (abs) return abs;
		}
	}

	// 4) dias/days — arrays con { image, icon, coverImage }
	const dias = pageData.dias ?? pageData.days;
	if (Array.isArray(dias)) {
		for (const day of dias) {
			const u = day?.image ?? day?.icon ?? day?.coverImage ?? day?.src;
			if (u) {
				const abs = toAbsolute(u);
				if (abs) return abs;
			}
		}
	}

	// 5) Búsqueda recursiva: recorre el objeto buscando cualquier string que parezca URL de imagen
	const found = deepFindImageUrl(pageData, 0, apiOrigin);
	if (found) return found;

	return null;
}

/**
 * Recorre recursivamente un objeto buscando la primera URL que parezca
 * una imagen (http..., data:image..., etc.)
 * Máx profundidad 6 para no colapsar en objetos enormes.
 */
function deepFindImageUrl(obj, depth = 0, apiOrigin = "") {
	if (depth > 6) return null;
	if (!obj || typeof obj !== "object") return null;

	const imageUrlPattern =
		/\.(jpe?g|png|webp|gif|bmp|avif|svg)(\?.*)?$/i;
	const dataImagePattern = /^data:image\/(png|jpe?g|webp|gif)/;
	const uploadPathPattern = /^\/uploads?\//;

	const isImageUrl = (str) => {
		if (typeof str !== "string") return false;
		const s = str.trim();
		if (!s) return false;
		if (s.startsWith("data:image/svg")) return false; // Skip SVG data URIs (too small)
		if (dataImagePattern.test(s)) return true;
		if (imageUrlPattern.test(s)) return true;
		if (uploadPathPattern.test(s)) return true;
		return false;
	};

	const toAbsolute = (url) => {
		if (!url || typeof url !== "string") return null;
		url = url.trim();
		if (!url) return null;
		if (url.startsWith("data:")) return url;
		if (url.startsWith("http://") || url.startsWith("https://")) return url;
		if (apiOrigin && url.startsWith("/"))
			return `${apiOrigin.replace(/\/$/, "")}${url}`;
		return url;
	};

	// Claves comunes para URLs de imagen (priorizar estas)
	const priorityKeys = [
		"src",
		"imageUrl",
		"url",
		"image",
		"coverImage",
		"backgroundImage",
		"bgImage",
		"portada",
		"icon",
	];

	// Primero buscar en claves prioritarias del nivel actual
	for (const key of priorityKeys) {
		if (obj[key] && typeof obj[key] === "string" && isImageUrl(obj[key])) {
			return toAbsolute(obj[key]);
		}
	}

	// Luego recorrer todas las propiedades
	const entries = Array.isArray(obj)
		? obj.map((v, i) => [i, v])
		: Object.entries(obj);

	for (const [, value] of entries) {
		if (typeof value === "string" && isImageUrl(value)) {
			return toAbsolute(value);
		}
		if (value && typeof value === "object") {
			const result = deepFindImageUrl(value, depth + 1, apiOrigin);
			if (result) return result;
		}
	}

	return null;
}

/**
 * Dada una lista de páginas (del API), busca la primera imagen útil
 * recorriendo todas las páginas en orden.
 * @param {Array} pages - Array de páginas (cada una con .data)
 * @param {string} apiOrigin
 * @returns {string|null}
 */
export function extractCoverFromPages(pages, apiOrigin = "") {
	if (!Array.isArray(pages) || pages.length === 0) return null;
	for (const page of pages) {
		const data = page?.data;
		if (!data) continue;
		const url = extractFirstPageCoverImage(data, apiOrigin);
		if (url) return url;
	}
	return null;
}
